/**
 * API Helper utilities for consistent API response handling in web application
 * Ported from mobile app for consistency
 */

import { processImageUrls } from './urlHelpers';

/**
 * Standard API response wrapper with error handling
 * @param {Function} apiCall - The API call function
 * @param {Object} options - Configuration options
 * @returns {Promise} Processed API response
 */
export const withApiErrorHandling = async (apiCall, options = {}) => {
  const {
    defaultValue = null,
    transformData = (data) => data,
    logErrors = true
  } = options;

  try {
    const response = await apiCall();
    return transformData(response.data);
  } catch (error) {
    if (logErrors) {
      console.error('API call error:', {
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        stack: error.stack
      });
    }
    
    // For authentication errors, throw the error instead of returning default
    if (error.response?.status === 404 || error.response?.status === 401 || error.response?.status === 422) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || `HTTP ${error.response?.status}: ${error.response?.statusText}`;
      throw new Error(errorMessage);
    }
    
    // For network errors or other issues, also throw with context
    if (!error.response) {
      throw new Error(`Network error: ${error.message}`);
    }
    
    // Return default value for other errors
    return defaultValue;
  }
};

/**
 * Ensure API response is an array (for list endpoints)
 * @param {any} data - API response data
 * @param {string} entityName - Name of entity for logging
 * @returns {Array} Always returns an array
 */
export const ensureArray = (data, _entityName = 'items') => {
  if (Array.isArray(data)) {
    return data;
  }
  
  if (data && typeof data === 'object') {
    // Handle paginated responses or wrapped data
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.results)) return data.results;
  }
  
  return [];
};

/**
 * Extract tenant information from current URL or domain
 * @returns {object|null} Tenant info object with method and identifier
 */
export const getTenantInfo = () => {
  const hostname = window.location.hostname.toLowerCase();
  const path = window.location.pathname;
  
  // Check if we're on localhost (development)
  if (hostname.startsWith('localhost') || hostname === '127.0.0.1') {
    // For development, check if there's a slug in the path
    const segments = path.split('/').filter(Boolean);
    if (segments.length > 0 && segments[0] !== 'api') {
      return {
        method: 'slug',
        domain: null,
        slug: segments[0]
      };
    }
    // If no slug, use default tenant for localhost
    return {
      method: 'slug',
      domain: null,
      slug: 'test_salon' // Default tenant for development
    };
  }
  
  // Helper function to strip common subdomains
  const stripSubdomains = (domain) => {
    const prefixes = ['app.', 'adm.', 'admin.'];
    for (const prefix of prefixes) {
      if (domain.startsWith(prefix)) {
        return domain.substring(prefix.length);
      }
    }
    return domain;
  };

  const baseDomain = stripSubdomains(hostname);

  // Slug-based domains (main platform domains that use URL path for tenant identification)
  const slugBasedDomains = ['entuso.com.br', 'vervio.com.br', 'vps62024.publiccloud.com.br'];

  // Check if we're on a custom domain (not slug-based domains)
  if (hostname && !slugBasedDomains.includes(baseDomain)) {
    return {
      method: 'domain',
      domain: baseDomain,
      slug: null // Domain-based tenants don't need slug in URLs
    };
  }
  
  // For slug-based tenants on main domain, first path segment is always the tenant slug
  const segments = path.split('/').filter(Boolean);
  if (segments.length > 0 && segments[0] !== 'api') {
    return {
      method: 'slug',
      domain: null,
      slug: segments[0]
    };
  }
  
  return null;
};

/**
 * Extract tenant slug from current URL (legacy compatibility)
 * @returns {string|null} Tenant slug or null if not found
 */
export const getTenantSlugFromUrl = () => {
  const tenantInfo = getTenantInfo();
  return tenantInfo?.slug || null;
};

/**
 * Build API endpoint URL with version prefix and tenant context
 * @param {string} endpoint - API endpoint
 * @param {string} version - API version (default: v1)
 * @param {Object} options - Options object
 * @param {boolean} options.isPublic - Force public endpoint (no tenant context)
 * @param {string} options.tenantSlug - Override tenant slug (auto-detected if not provided)
 * @returns {string} Full API endpoint
 */
export const buildApiEndpoint = (endpoint, version = 'v1', options = {}) => {
  const { isPublic = false, tenantSlug = null } = options;
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  
  if (isPublic) {
    return `/api/${version}/${cleanEndpoint}`;
  }
  
  // Get tenant info to determine how to build the URL
  const tenantInfo = getTenantInfo();
  
  if (tenantInfo) {
    if (tenantInfo.method === 'domain') {
      // Domain-based tenant: clean URLs without slug
      return `/api/${version}/${cleanEndpoint}`;
    } else if (tenantInfo.method === 'slug') {
      // Slug-based tenant: include slug in URL for backward compatibility
      const slug = tenantSlug || tenantInfo.slug;
      return `/api/${version}/${slug}/${cleanEndpoint}`;
    }
  }
  
  // Fallback for when no tenant is detected - provide detailed error info
  const errorInfo = {
    hostname,
    path,
    url: window.location.href,
    tenantInfo,
    endpoint: cleanEndpoint
  };
  
  console.error('Tenant context detection failed:', errorInfo);
  throw new Error(`Tenant context is required for API calls. URL: ${window.location.href}, Path: ${path}, Hostname: ${hostname}`);
};

/**
 * Normalize email or phone input for API requests
 * @param {string} input - Email or phone input
 * @returns {string} Normalized input
 */
export const normalizeEmailOrPhone = (input) => {
  if (!input) return input;
  
  // Import here to avoid circular dependencies
  const { normalizeEmailOrPhone: normalize } = import('./phoneUtils');
  return normalize(input);
};

/**
 * Standard error message formatter
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const formatErrorMessage = (error) => {
  if (error.response?.status === 401) {
    return 'Credenciais inválidas';
  } else if (error.response?.status >= 500) {
    return 'Erro no servidor. Tente novamente.';
  } else if (error.code === 'ECONNABORTED') {
    return 'Tempo limite excedido. Verifique sua conexão.';
  }
  
  return error.response?.data?.detail || error.message || 'Erro ao processar solicitação';
};

/**
 * Standard transformer for entities with images
 * @param {Object|Array} data - Entity data
 * @param {string[]} imageFields - Image field names
 * @returns {Object|Array} Data with processed image URLs
 */
export const transformEntityWithImages = (data, imageFields = []) => {
  if (!data) return data;
  
  if (Array.isArray(data)) {
    return data.map(item => processImageUrls(item, imageFields));
  }
  
  return processImageUrls(data, imageFields);
};

/**
 * Standard CRUD operations factory
 * @param {Object} config - Configuration object
 * @returns {Object} CRUD operations
 */
export const createCrudApi = (config) => {
  const {
    endpoint,
    imageFields = [],
    entityName = 'item'
  } = config;

  const apiEndpoint = buildApiEndpoint(endpoint);

  return {
    // Get all items
    getAll: async (params = {}) => {
      return withApiErrorHandling(
        () => {
          const apiClient = import('../config/api').default;
          return apiClient.get(apiEndpoint, { params });
        },
        {
          defaultValue: [],
          transformData: (data) => {
            const items = ensureArray(data, entityName);
            return transformEntityWithImages(items, imageFields);
          }
        }
      );
    },

    // Get item by ID
    getById: async (id) => {
      return withApiErrorHandling(
        () => {
          const apiClient = import('../config/api').default;
          return apiClient.get(`${apiEndpoint}/${id}`);
        },
        {
          defaultValue: null,
          transformData: (data) => transformEntityWithImages(data, imageFields)
        }
      );
    },

    // Create new item
    create: async (data) => {
      return withApiErrorHandling(
        () => {
          const apiClient = import('../config/api').default;
          return apiClient.post(apiEndpoint, data);
        },
        {
          defaultValue: null,
          transformData: (data) => transformEntityWithImages(data, imageFields)
        }
      );
    },

    // Update item
    update: async (id, data) => {
      return withApiErrorHandling(
        () => {
          const apiClient = import('../config/api').default;
          return apiClient.put(`${apiEndpoint}/${id}`, data);
        },
        {
          defaultValue: null,
          transformData: (data) => transformEntityWithImages(data, imageFields)
        }
      );
    },

    // Delete item
    delete: async (id) => {
      return withApiErrorHandling(
        () => {
          const apiClient = import('../config/api').default;
          return apiClient.delete(`${apiEndpoint}/${id}`);
        },
        {
          defaultValue: true,
          transformData: () => true
        }
      );
    }
  };
};